/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import React from "react";

import { AuthenticatedTemplate, useIsAuthenticated, useMsal } from "@azure/msal-react";
import { Stack, Text, Link } from "@fluentui/react";
import { SignInButton } from "./SignInButton";
import { SignOutButton } from "./SignOutButton";
import { TokenButton } from "./TokenButton";
import { loginRequest } from "../lib/authConfig";
import { callMsGraph } from "../lib/graph";
import styles from "./PageLayout.module.scss";

/**
* Renders information about the signed-in user or a button to retrieve data about the user
*/
const ProfileContent = () => {
    const { instance, accounts } = useMsal();
    const [graphData, setGraphData] = React.useState<{ displayName: string, mail: string } | null>(null);

    const requestProfileData = React.useCallback(() => {
        if (accounts.length > 0) {
            // Silently acquires an access token which is then attached to a request for MS Graph data
            instance
                .acquireTokenSilent({
                    ...loginRequest,
                    account: accounts[0],
                })
                .then((response) => {
                    callMsGraph(response.accessToken).then((response) => setGraphData(response));
                });
        }
    }, [instance, accounts]);

    React.useEffect(() => {
        if (!graphData) {
            requestProfileData();
        }
    }, [graphData, requestProfileData]);

    return (
        <Text variant="large" className={styles.usernameText}>
            {graphData
                ? `${graphData.displayName} (${graphData.mail})`
                : "Loading..."}
        </Text>
    );
};

/**
 * Renders the navbar component with a sign in or sign out button depending on whether or not a user is authenticated
 * @param props
 */
export const PageLayout = ({ children }: { children: JSX.Element | null; }) => {
    const isAuthenticated = useIsAuthenticated();

    return (
        <>
            <Stack
                className={styles.navbar}
                horizontal
                horizontalAlign="space-between"
                verticalAlign="center"
            >
                <Link href="/" className={styles.titleLink}>
                    <Text variant="xLarge" className={styles.title}>OfficePLUS Clippy Backend Portal</Text>
                </Link>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={{ childrenGap: 10 }}>
                    <AuthenticatedTemplate>
                        <ProfileContent />
                        <TokenButton />
                    </AuthenticatedTemplate>
                    {isAuthenticated ? <SignOutButton /> : <SignInButton />}
                </Stack>
            </Stack>
            {children}
        </>
    );
};
